import useTranslator from "../../hooks/useTranslator";
import { WORKSPACE_ROLES_CAPABILITIES } from "../../Utils/workspaceRoleCapabilities";
import { useContext, useEffect, useRef, useState } from "react";
import AuthContext from "../../context/auth/AuthContext";
import {
  RowButton,
  Button,
  Control,
  Field,
  Icon,
  Modal,
  TextField,
} from "../design";
import "./workspaceModals.css";

const InviteMembersModal = (props) => {
  const {
    isOpen,
    onClose,
    setMemberData,
    memberData,
    handleAddMember,
    handleRemoveMember,
    handleSubmit,
    handleGoBackToInviteCustomization,
    disableButtons,
    workspaceName,
    goToPrevStep,
    goToStepSix,
    goToStepFive,
    goToStepFour,
  } = props;
  const { _t } = useTranslator();

  const { user } = useContext(AuthContext);
  const [roleDropdown, setRoleDropdown] = useState(false);
  const roleDropdownRefs = useRef({});

  const handleInputChange = (index, field, value) => {
    setMemberData((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      return updated;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const activeRef = roleDropdownRefs.current[roleDropdown];

      if (activeRef && !activeRef.contains(event.target)) {
        setRoleDropdown(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [roleDropdown]);

  const goBack = () => {
    handleGoBackToInviteCustomization();
    goToStepFour();
  };

  return (
    <Modal
      open={isOpen}
      onClose={() => {
        goBack();
        onClose();
      }}
      id="invite-member-walkthrough"
      size="md"
      align="start"
      title={_t("inviteMembers")}
      description={
        <>
          {_t("inviteMembersTo")} <strong>{workspaceName}</strong>{" "}
          {_t("workspace")}
        </>
      }
      actions={
        <>
          <Button
            variant="secondary"
            size="lg"
            disabled={disableButtons}
            onClick={goBack}
          >
            {_t("goBack")}
          </Button>
          <Button size="lg" loading={disableButtons} onClick={handleSubmit}>
            {_t("createWorkspace")}
          </Button>
        </>
      }
    >
      <div className="invite-members-list">
        {memberData.map((member, index) => (
          <div
            key={index}
            className="invite-member-row"
            ref={(el) => (roleDropdownRefs.current[index] = el)}
          >
            <TextField
              id={`invite-member-email-${index}`}
              label={index === 0 ? _t("email") : undefined}
              type="email"
              value={member.email}
              placeholder={_t("enterEmail")}
              onChange={(e) =>
                handleInputChange(index, "email", e.target.value?.toLowerCase())
              }
              name="email"
              required
            />
            <Field label={index === 0 ? _t("role") : undefined}>
              <div className="invite-member-role">
                <Control chevron>
                  <RowButton
                    type="button"
                    className={`invite-member-role__trigger ${
                      member.role ? "" : "is-placeholder"
                    }`}
                    aria-expanded={roleDropdown === index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setRoleDropdown(roleDropdown === index ? -1 : index);
                    }}
                  >
                    {member.role
                      ? _t(`${member.role?.toLowerCase()}Member`)
                      : _t("pleaseSelect")}
                  </RowButton>
                </Control>
              </div>
            </Field>
            {index > 0 ? (
              <Button
                variant="ghost"
                size="sm"
                className="invite-member-remove"
                aria-label={_t("remove")}
                onClick={() => handleRemoveMember(index)}
              >
                <Icon name="close" size={16} />
              </Button>
            ) : (
              <span className="invite-member-remove" aria-hidden="true" />
            )}
            {roleDropdown === index && (
              <div className="invite-member-role__menu" role="listbox">
                {WORKSPACE_ROLES_CAPABILITIES?.roleHierarchyBasedOnIndex
                  ?.filter((role) => role !== "OWNER")
                  ?.map((role) => (
                    <RowButton
                      type="button"
                      role="option"
                      key={role}
                      aria-selected={member.role === role}
                      className={`invite-member-role__option ${
                        member.role === role ? "is-selected" : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleInputChange(index, "role", role);
                        setRoleDropdown(-1);
                      }}
                    >
                      <span className="invite-member-role__name">
                        {_t(`${role?.toLowerCase()}Member`)}
                      </span>
                      <span className="invite-member-role__text">
                        {_t(`${role?.toLowerCase()}Description`)}
                      </span>
                    </RowButton>
                  ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="invite-member-add"
        iconStart={<Icon name="plus" size={16} />}
        onClick={handleAddMember}
      >
        {_t("addMoreMember")}
      </Button>
    </Modal>
  );
};

export default InviteMembersModal;
